import { brandLinka } from '@ads/tokens';
import { DashboardCouponUsage } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import { ChartCard, ChartCardContent, ChartCardHeader, ChartCardTitle, ExportChartTriggerButton } from '../components';

interface CouponsChartProps {
  data: DashboardCouponUsage;
}
export const CouponsChart = ({ data }: CouponsChartProps) => {
  const t = useTranslation();
  const chartConfig = {
    couponCode: { label: t('coupon') },
    usageCount: { label: t('usage-count'), color: brandLinka.contextualColor.interactive.primary },
  };
  return (
    <ChartCard className='lg:col-span-2'>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.coupon-distribution')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_COUPONS' />
      </ChartCardHeader>
      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex'>
          <ChartContainer className='w-[60px]' config={chartConfig}>
            <BarChart data={data.data} responsive accessibilityLayer margin={{ top: 24 }}>
              <Bar dataKey='usageCount' fill='transparent' barSize={0} />
              <YAxis dataKey='usageCount' tickLine={false} tickMargin={10} axisLine={false} width={60} />
              <XAxis dataKey='couponCode' height={100} />
            </BarChart>
          </ChartContainer>

          <div className='flex-1 overflow-x-auto pb-xs'>
            <ChartContainer className='w-full' config={chartConfig} dataLength={data.data.length}>
              <BarChart data={data.data} responsive accessibilityLayer margin={{ top: 24 }}>
                <CartesianGrid vertical={false} />
                <Bar dataKey='usageCount' fill='var(--color-usageCount)' radius={[8, 8, 0, 0]}>
                  <LabelList dataKey='usageCount' position='top' fontSize={14} />
                </Bar>
                <XAxis
                  dataKey='couponCode'
                  tickLine={false}
                  tickMargin={5}
                  axisLine={false}
                  tick={{ textAnchor: 'start' }}
                  angle={45}
                  height={100}
                  tickFormatter={(label) => {
                    const labelLength = String(label).length;
                    const maxLength = 10;
                    return labelLength > maxLength ? `${String(label).substring(0, maxLength)}...` : label;
                  }}
                />
                <ChartTooltip
                  cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                  content={<ChartTooltipContent />}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </ChartCardContent>
      )}
    </ChartCard>
  );
};
